import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import PageArea from '../../shared/PageArea';
import { PageTitleWithButtons } from '../../shared/layout/PageTitleWithButtons';
import { DetailPageSkeleton } from './DetailPageSkeleton';
import { TabbedSection } from '../../shared/tabs/TabbedSection';
import type { TabDefinition } from '../../shared/tabs/TabbedSection';
import { InsightTileGroup } from '../../shared/tiles/InsightTileGroup';
import type { InsightTileGroupLayout, InsightTileGroupProps } from '../../shared/tiles/InsightTileGroup';

// Detail pages default to the picture+six-tile layout; set `layout` explicitly to opt into another one.
type PictureSixHeadline = Extract<InsightTileGroupProps, { layout: 'picture-six' }>;
type OtherDetailHeadline = Exclude<InsightTileGroupProps, { layout: 'picture-six' }>;
export type DetailPageHeadline =
  | (Omit<PictureSixHeadline, 'layout'> & { layout?: 'picture-six' })
  | OtherDetailHeadline;

export interface DetailPageTemplateProps {
  title: string;
  subtitle?: ReactNode;
  backButton?: ReactNode;
  actions?: ReactNode;
  headlineContent: DetailPageHeadline;
  tabs?: readonly TabDefinition[];
  activeTabId?: string;
  onTabChange?: (tabId: string) => void;
  tabsAriaLabel?: string;
  tabsContentPadding?: boolean;
  tabsContentSx?: SxProps<Theme>;
  children?: ReactNode;
  loading?: boolean;
  loadingLabel?: string;
  showPictureInSkeleton?: boolean;
  insightCountInSkeleton?: number;
  headlineLayoutInSkeleton?: InsightTileGroupLayout;
  showTabsInSkeleton?: boolean;
  sx?: SxProps<Theme>;
}

export const DetailPageTemplate = ({
  title,
  subtitle,
  backButton,
  actions,
  headlineContent,
  tabs,
  activeTabId,
  onTabChange,
  tabsAriaLabel,
  tabsContentPadding = true,
  tabsContentSx,
  children,
  loading = false,
  loadingLabel,
  showPictureInSkeleton,
  insightCountInSkeleton = 6,
  headlineLayoutInSkeleton,
  showTabsInSkeleton = false,
  sx,
}: DetailPageTemplateProps) => {
  const resolvedHeadline = {
    layout: 'picture-six',
    ...headlineContent,
  } as InsightTileGroupProps;
  // Skeleton shape defaults to the actual headline layout so loading never shows a picture/tile count that the real content won't have.
  const resolvedShowPictureInSkeleton = showPictureInSkeleton
    ?? (resolvedHeadline.layout === 'picture-six' || resolvedHeadline.layout === 'picture-six-wide');
  const resolvedHeadlineLayoutInSkeleton = headlineLayoutInSkeleton ?? resolvedHeadline.layout;
  const shouldShowTabsInSkeleton = showTabsInSkeleton || Boolean(tabs?.length);

  if (loading) {
    return (
      <PageArea>
        <DetailPageSkeleton
          showPicture={resolvedShowPictureInSkeleton}
          insightCount={insightCountInSkeleton}
          headlineLayout={resolvedHeadlineLayoutInSkeleton}
          showTabs={shouldShowTabsInSkeleton}
          loadingLabel={loadingLabel}
          sx={sx}
        />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Box sx={sx}>
        {backButton !== undefined && backButton !== null && (
          <Box sx={{ mb: 3 }}>
            {backButton}
          </Box>
        )}

        <PageTitleWithButtons
          title={title}
          subTitle={subtitle ?? ''}
        >
          {actions !== undefined && actions !== null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'nowrap',
                flexShrink: 0,
              }}
            >
              {actions}
            </Box>
          )}
        </PageTitleWithButtons>

        <Box sx={{ mt: 6 }}>
          <InsightTileGroup {...resolvedHeadline} />
        </Box>

        {tabs && tabs.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <TabbedSection
              tabs={tabs}
              activeTabId={activeTabId}
              onTabChange={onTabChange}
              ariaLabel={tabsAriaLabel}
              contentPadding={tabsContentPadding}
              contentSx={tabsContentSx}
            />
          </Box>
        )}

        {children !== undefined && children !== null && (
          <Box sx={{ mt: 6 }}>
            {children}
          </Box>
        )}
      </Box>
    </PageArea>
  );
};
